import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/context/CompanyContext';

export type ManagementMode = 'caisse' | 'retrait' | null;

interface ManagementModeSettings {
  mode: ManagementMode;
}

export function useManagementMode() {
  const [mode, setMode] = useState<ManagementMode>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { currentCompany } = useCompany();

  // Charger le mode depuis la base de données
  useEffect(() => {
    const loadMode = async () => {
      if (!currentCompany) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('company_settings')
          .select('management_mode')
          .eq('company_id', currentCompany.id)
          .maybeSingle();

        if (error) {
          console.error('Error loading management mode:', error);
          setMode(null);
        } else if (data?.management_mode) {
          setMode(data.management_mode as ManagementMode);
        } else {
          setMode(null);
        }
      } catch (error) {
        console.error('Error loading management mode:', error);
        setMode(null);
      } finally {
        setLoading(false);
      }
    };

    loadMode();
  }, [currentCompany]);

  // Sauvegarder le mode dans la base de données
  const updateMode = async (newMode: ManagementMode) => {
    if (!currentCompany || !newMode) return false;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('company_settings')
        .upsert({
          company_id: currentCompany.id,
          management_mode: newMode,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'company_id'
        });

      if (error) {
        console.error('Error updating management mode:', error);
        toast({
          title: "Erreur",
          description: "Impossible de sauvegarder le mode de gestion",
          variant: "destructive"
        });
        return false;
      }

      setMode(newMode);
      toast({
        title: "Mode configuré",
        description: `Mode de gestion "${newMode}" activé avec succès`,
      });
      return true;
    } catch (error) {
      console.error('Error updating management mode:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la sauvegarde",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    mode,
    loading,
    updateMode,
    hasMode: mode !== null,
    isCaisseMode: mode === 'caisse',
    isRetraitMode: mode === 'retrait'
  };
}